import { AgGridReact } from 'ag-grid-react';
import { Theme, themeQuartz } from "ag-grid-community";
import React, { useEffect, useRef, useState, useMemo } from 'react';
import Loader from '../../../../components/loader';
import { titleCell } from '../../../../components/elements/function';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useWorkOrderApi } from '../../../../store/api/hooks/workOrderApiHook';
import toast from 'react-hot-toast';
import { downloadExcel, downloadPDF, showLocalDateTime } from '../../../../_common/functions';
import Button from '../../../../components/elements/button';
import { filterConfigType } from '../../../../_config/types';
import Filters from '../../../../components/filters';
import Icon from '../../../../components/icons';
import { DATE_FORMAT, PAGE_SIZE } from '../../../../_common/constant';
import Pagination from '../../../../components/pagination';
import ChecklistLogPreviewModal from './checklistLogPreview.modal';
import { URLS } from '../../../../_config';

const ChecklistLog: React.FC = () => {
    const myTheme = themeQuartz.withParams({
        headerBackgroundColor: `#F0F0F0`
    });
    const theme = useMemo<Theme | "legacy">(() => {
        return myTheme;
    }, []);
    const params: any = useParams();
    const [rowData, setRowData] = useState<any[]>([]);
    const workOrderApi: any = useWorkOrderApi();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [gridApi, setGridApi] = useState<any>(null);
    const [filters, setFilters] = useState<any>({});
    const filtersRef: any = useRef();

    const previewRef = useRef<HTMLDialogElement>(null);
    const [previewData, setPreviewData] = useState<any>(null);

    const filterConfig: filterConfigType[] = [
        { name: 'Start Date', type: 'date', key: 'startDate' },
        { name: 'End Date', type: 'date', key: 'endDate' },
    ];

    const onPreviewOpen = (data: any) => {
        setPreviewData(data);
        previewRef.current?.showModal();
    };

    const columnDefs = [
        {
            headerName: 'WO Number',
            field: 'workOrderCode',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 150,
            cellRenderer: (params: any) => (
                <p className="hover:text-primary font-medium cursor-pointer truncate" title={params.value}>
                    {params.value}
                </p>
            ),
            onCellClicked: (param: any) => {
                const url = URLS.WORK_ORDER_DETAILS(param.data.workOrderID);
                window.open(url, '_blank');
            },
        },
        {
            headerName: 'Checklist Number',
            field: 'checkListCode',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 150,
            cellRenderer: (params: any) => (
                <p
                    className="hover:text-primary font-medium cursor-pointer truncate"
                    title={params.value}
                    onClick={() => onPreviewOpen(params.data)}
                >
                    {params.value}
                </p>
            ),
        },
        {
            headerName: 'Checklist Name',
            field: 'checkListName',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 150,
            cellRenderer: (params: any) => <p className="truncate">{titleCell(params)}</p>,
        },
        {
            headerName: 'Performed By',
            field: 'addedByName',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 120,
            cellRenderer: (params: any) => <p className="truncate">{titleCell(params)}</p>,
        },
        {
            headerName: 'Performed Date Time',
            field: 'addedDate',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 140,
            cellRenderer: (data: any) => (
                <p className="hover:text-primary font-medium cursor-pointer truncate" title={data.value ? showLocalDateTime(data.value) : '--'}>
                    {data.value ? showLocalDateTime(data.value) : '--'}
                </p>
            ),
        },
    ];

    const getSelectedRows = () => {
        if (!gridApi) return [];
        const selectedNodes = gridApi.getSelectedNodes();
        return selectedNodes.map((node: any) => node.data);
    };

    const onBulkExportPdf = () => {
        const selectedData = getSelectedRows();
        if (selectedData.length === 0) {
            toast.error("Nothing is selected");
        } else {
            const payload = { assetID: params.id };
            workOrderApi.GetWOTaskLogExportToPDF(
                payload,
                (resp: any) => downloadPDF(resp, 'ChecklistLogPdf'),
                (error: any) => toast.error(error)
            );
        }
    };

    const onBulkExportCsv = () => {
        const selectedData = getSelectedRows();
        if (selectedData.length === 0) {
            toast.error("Nothing is selected");
        } else {
            const payload = { assetID: params.id };
            workOrderApi.GetWOTaskLogExportToXLS(
                payload,
                (resp: any) => downloadExcel(resp, 'ChecklistLogXls'),
                (error: any) => toast.error(error)
            );
        }
    };

    const GetWorkOrderTaskLog = () => {
        const payload: any = {
            assetID: params.id,
            pageSize: PAGE_SIZE,
            pageNumber: currentPage,
        };

        if (filters) {
            Object.keys(filters).forEach((key) => {
                if (filters[key]) {
                    payload[key] = ['startDate', 'endDate'].includes(key)
                        ? moment(filters[key]).format('YYYY-MM-DD')
                        : filters[key];
                }
            });
        }

        setRowData([]);
        workOrderApi.GetWorkOrderTaskLog(
            payload,
            (resp: any) => {
                setRowData(resp.taskLogList || []);
                setTotalRecords(resp.totalRecord || 0);
            },
            (error: any) => toast.error(error)
        );
    };

    useEffect(() => {
        if (params.id) GetWorkOrderTaskLog();
    }, [params.id, currentPage, filters]);

    const onGridReady = (params: any) => {
        setGridApi(params.api);
    };

    return (
        <div>
            <div className="flex items-center justify-between h-[50px]">
                <h4 className="font-semibold text-[0.938rem]">Checklist Log</h4>
            </div>

            <div className={`mb-3 ag-theme-alpine overflow-auto ${!rowData || rowData.length === 0 || rowData.length <= 2 ? 'h-[150px]' : 'h-[300px]'}`}>
                <AgGridReact
                    theme={theme}
                    columnDefs={columnDefs}
                    rowData={rowData || []}
                    pagination={false}
                    domLayout="normal"
                    ensureDomOrder={true}
                    suppressHorizontalScroll={false}
                    loadingOverlayComponent={Loader}
                    noRowsOverlayComponent={() => (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500">
                                <div className="text-lg font-medium">No Data Found</div>
                            </div>
                        </div>
                    )}
                />
            </div>

            <Filters _ref={filtersRef} config={filterConfig} filters={filters} onUpdate={setFilters} />

            <ChecklistLogPreviewModal _ref={previewRef} workOrderID={previewData?.workOrderID} />
        </div>
    );
};

export default ChecklistLog;